import { forwardRef, useImperativeHandle, useRef } from 'react';

import { TIMESERIES_CHART_ARIA_LABEL } from './constants.js';
import { useTimeseriesChartConfig } from './hooks/use-timeseries-chart-config.js';
import { iterateSlots } from './iterate-slots.js';
import {
  mapSelectedSeriesToTimeseries,
  mapSelectionDomainToTimeseries,
} from './map-select-actions.js';
import {
  mapBandSeriesActionsToXY,
  mapSeriesActionsToXY,
} from './map-series-actions.js';
import { resolveValueRepresentation } from './resolve-value-representation.js';
import { transformTimeseriesAnnotationsToXY } from './transformer-annotations-to-xy.js';
import { transformTimeseriesDataToXYChart } from './transformer-data-to-xy.js';
import {
  type ChartVariantInternal,
  DEFAULT_VARIANT,
  type TimeseriesChartProps,
  type TimeseriesChartRef,
} from './types/timeseries.js';
import { buildBuckets } from './utils/build-buckets.js';
import { buildTimeseriesImperativeHandle } from './utils/build-timeseries-imperative-handle.js';
import { deriveXYChartPropsFromConfig } from './utils/derive-xychart-props-from-config.js';
import { formatCSV } from './utils/download/format-csv.js';
import {
  mapXAxisSlot,
  mapYAxisSlots,
  mapYAxisSlotToProps,
} from './utils/map-axis-slots.js';
import { resolveTimeseriesColorRules } from './utils/map-timeseries-to-xy-color-rules.js';
import {
  defineAxisId,
  renderAreaSeries,
  renderBandSeries,
  renderBarSeries,
  renderLineSeries,
  xAxisId,
} from './utils/render-series.js';
import { resolveGapPolicy } from './utils/resolve-gap-policy.js';
import { mapTimeseriesToXYThresholds } from './utils/transformer-thresholds-to-xy.js';
import { useBehavioralTrackingProps } from '../../../core/hooks/useBehavioralTrackingProps.js';
import { downloadCSV } from '../../../core/utils/download.js';
import { getChartStatesTemplates } from '../../core/components/chart-states/utils/get-chart-states-template.js';
import { CSS_DOWNLOAD_FILENAME } from '../../core/constants.js';
import { extractBehaviorTrackingProps } from '../../core/utils/extract-behavior-tracking-props.js';
import type { XYChartRef } from '../../xy-chart/types/xy-chart.js';
import { XYChart } from '../../xy-chart/XYChartRoot.js';
import { renderXYAnnotations } from '../shared/utils/render-xy-annotations.js';

export const TimeseriesToXY = forwardRef<
  TimeseriesChartRef,
  TimeseriesChartProps
>((props, forwardedRef) => {
  const {
    data = [],
    variant = DEFAULT_VARIANT,
    children,
    loading,
    className,
    style,
    width,
    height,
    colorPalette,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    truncationMode,
    valueRepresentation,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
    seriesActions,
    gapPolicy,
    pointsDisplay,
    curve,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'aria-describedby': ariaDescribedBy,
    'aria-details': ariaDetails,
  } = props;

  const [behaviorTrackingProps] = useBehavioralTrackingProps(props);

  const config = useTimeseriesChartConfig(children, {
    colorPalette,
    truncationMode,
    valueRepresentation,
    gapPolicy,
    pointsDisplay,
    initialZoom,
    currentZoom,
    onZoomChange,
    infiniteZoom,
    curve,
  });

  const { line, bar, area, band, shapePriority, deprecatedToolbar } =
    iterateSlots(children);

  const resolvedValueRepresentation = resolveValueRepresentation(
    config.valueRepresentation,
    ...line.map((s) => s.valueRepresentation),
    ...area.map((s) => s.valueRepresentation),
    ...bar.map((s) => s.valueRepresentation),
  );

  const { emptyState, errorState } = getChartStatesTemplates(children);

  const {
    buckets,
    lineAreaBarData,
    bandData,
    allSeriesData,
    gapPolicies,
    order,
  } = buildBuckets(
    data,
    variant,
    { line, area, bar, band, shapePriority },
    config.pointsDisplay,
  );

  const result = transformTimeseriesDataToXYChart(buckets, order);
  const effectiveGapPolicy = resolveGapPolicy(gapPolicies, config.gapPolicy);

  const allColorRules = resolveTimeseriesColorRules(
    allSeriesData,
    config.colorRules,
  );

  const xAxisConfig = mapXAxisSlot(config.xAxis);

  const { configuredAxes, renderedUnits } = mapYAxisSlots(
    config.yAxis.left,
    config.yAxis.right,
    [...result.units],
  );

  const yAxesConfig = renderedUnits.map((unit, index) => ({
    unit,
    axisId: defineAxisId(unit),
    ...mapYAxisSlotToProps(index, configuredAxes[index]),
  }));

  const flatThresholds = config.thresholds
    ? [...(config.thresholds.left ?? []), ...(config.thresholds.right ?? [])]
    : [];
  const mappedThresholds = mapTimeseriesToXYThresholds(
    flatThresholds,
    yAxesConfig,
  );

  const mappedSeriesActions = seriesActions
    ? mapSeriesActionsToXY(seriesActions, lineAreaBarData)
    : undefined;

  const mappedBandActions = seriesActions
    ? mapBandSeriesActionsToXY(seriesActions, bandData)
    : undefined;

  const seriesForShape = (shape: ChartVariantInternal) => {
    switch (shape) {
      case 'line':
        return result.line.map((s) =>
          renderLineSeries(s, {
            actions: mappedSeriesActions,
            curve: config.curve,
          }),
        );
      case 'area':
        return result.area.map((s) =>
          renderAreaSeries(s, {
            actions: mappedSeriesActions,
            curve: config.curve,
          }),
        );
      case 'bar':
        return result.bar.map((s) =>
          renderBarSeries(s, { actions: mappedSeriesActions }),
        );
      case 'band':
        return result.band.map((s) =>
          renderBandSeries(s, {
            actions: mappedBandActions,
            curve: config.curve,
          }),
        );
    }
  };

  const {
    disableZoom,
    disablePan,
    toolbarEnabled,
    toolbarPlacement,
    toolbarIntents,
    onZoomChange: effectiveOnZoomChange,
  } = deriveXYChartPropsFromConfig(config, onZoomChange);

  const selectActions = config.select?.actions;
  const tooltipVariant =
    config.tooltip.variant === 'single' ? 'single' : 'shared';

  const xyRef = useRef<XYChartRef>(null);

  useImperativeHandle(
    forwardedRef,
    () => buildTimeseriesImperativeHandle(xyRef, config),
    [config],
  );

  const downloadDataCallback = () => {
    const csvString = formatCSV({
      line: buckets.line.map((s) => s.data),
      area: buckets.area.map((s) => s.data),
      bar: buckets.bar.map((s) => s.data),
      band: bandData,
    });
    downloadCSV(csvString, CSS_DOWNLOAD_FILENAME);
  };

  return (
    <XYChart
      data={[]}
      ref={xyRef}
      loading={loading}
      className={className}
      style={style}
      width={width}
      height={height}
      colorPalette={config.colorPalette}
      data-dtrum-mask={dataDtrumMask}
      data-dtrum-allow={dataDtrumAllow}
      truncationMode={config.truncationMode}
      __downloadDataCallback={downloadDataCallback}
      data-dt-component="TimeseriesChart"
      aria-label={ariaLabel ?? TIMESERIES_CHART_ARIA_LABEL}
      aria-labelledby={ariaLabelledBy}
      aria-describedby={ariaDescribedBy}
      aria-details={ariaDetails}
      {...behaviorTrackingProps}
    >
      <XYChart.Tooltip
        _variant={tooltipVariant}
        _seriesDisplayMode={config.tooltip.seriesDisplayMode}
      />
      {errorState && <XYChart.ErrorState>{errorState}</XYChart.ErrorState>}
      {emptyState && <XYChart.EmptyState>{emptyState}</XYChart.EmptyState>}
      {order.flatMap(seriesForShape)}
      {selectActions ? (
        <XYChart.Select
          actions={(selectedSeries, selectionDomain) => {
            const hasNoDataInSelection = selectionDomain.length === 0;
            if (hasNoDataInSelection) {
              return;
            }
            return selectActions(
              mapSelectedSeriesToTimeseries(selectedSeries, allSeriesData),
              mapSelectionDomainToTimeseries(selectionDomain),
            );
          }}
        />
      ) : null}
      <XYChart.XAxis
        id={xAxisId}
        type="time"
        position="bottom"
        min={xAxisConfig.min}
        max={xAxisConfig.max}
        label={xAxisConfig.label}
        disableZoom={disableZoom}
        disablePan={disablePan}
        initialZoom={
          config.initialZoom as [Date, Date] | [number, number] | undefined
        }
        currentZoom={
          config.currentZoom as [Date, Date] | [number, number] | undefined
        }
        onZoomChange={effectiveOnZoomChange}
        infiniteZoom={config.infiniteZoom}
        gapPolicy={effectiveGapPolicy}
      />
      {yAxesConfig.map(({ unit, axisId, ...axisProps }) => (
        <XYChart.YAxis
          key={axisId}
          id={axisId}
          unit={unit}
          {...axisProps}
          valueRepresentation={resolvedValueRepresentation}
        />
      ))}
      <XYChart.Legend {...config.legend} />
      {allColorRules.map((rule) => (
        <XYChart.ColorRule
          key={`${rule.comparator}-${rule.matchValue}-${rule.valueAccessor}`}
          {...rule}
        />
      ))}
      <XYChart.Toolbar
        hidden={!toolbarEnabled}
        placement={toolbarPlacement}
        {...extractBehaviorTrackingProps(config.toolbar ?? {})}
      />
      {/* TODO: Remove after deprecating ChartToolbar (APPDEV-15613) */}
      {deprecatedToolbar.enabled && (
        <XYChart.Toolbar placement={deprecatedToolbar.placement} />
      )}
      {toolbarIntents?.map((intentProps, index) => (
        <XYChart.Intent key={index} {...intentProps} />
      ))}
      {config.annotations &&
        renderXYAnnotations(
          transformTimeseriesAnnotationsToXY(config.annotations.tracks),
          xAxisId,
          config.annotations,
        )}
      {mappedThresholds.map((threshold, index) => (
        <XYChart.ThresholdIndicator key={index} {...threshold} />
      ))}
    </XYChart>
  );
});
